import { type CombinedError, type Exchange, mapExchange } from "urql";

export const RELOADED_BUILD_STORAGE_KEY = "acto.schemaMismatchReloadedBuild";

// graphql-java classifies both a document the schema rejects and a variable it cannot coerce (how a removed input field
// arrives) as a `ValidationError` without a `code`. The api's own business-rule `ValidationError`s carry a `code`.
function isSchemaMismatch(error: CombinedError) {
  return error.graphQLErrors.some(
    ({ extensions }) => extensions?.["classification"] === "ValidationError" && extensions["code"] === undefined,
  );
}

function readReloadedBuild(): string | null {
  try {
    return sessionStorage.getItem(RELOADED_BUILD_STORAGE_KEY);
  } catch {
    return null;
  }
}

function recordReloadedBuild(buildVersion: string): boolean {
  try {
    sessionStorage.setItem(RELOADED_BUILD_STORAGE_KEY, buildVersion);
    return true;
  } catch {
    return false;
  }
}

/**
 * Reloads the page when the api rejects a document as invalid against its schema, at most once per build per tab
 * session. A rejected mutation never reloads, so its input stays on screen. Without a build version, or without
 * session storage to record the reload, it never reloads.
 */
export function schemaMismatchReloadExchange(buildVersion: string | undefined): Exchange {
  return mapExchange({
    onError(error, operation) {
      if (!buildVersion || operation.kind === "mutation" || !isSchemaMismatch(error)) return;
      if (readReloadedBuild() === buildVersion) return;
      if (!recordReloadedBuild(buildVersion)) return;

      console.warn("The api rejected a query as invalid against its schema, reloading for the deployed build", error);
      window.location.reload();
    },
  });
}
